import { filter, sortBy } from 'lodash-es'
import type { NormalGraphEdge } from './normal-graph-edge'

/**
 * 获取圆心到point点中间与圆交叉的点
 * @param {*} arrowsWidth 箭头大小
 * @param {*} strokeWidth 线的大小
 * @param {*} nodeSize 实体大小
 * @param {*} source 起始点
 * @param {boolean} [isSubtractArrows=true] // 计算过程是否减去箭头宽度
 * @returns {Object} 交叉点
 */
export function getLinkPoint({
  arrowsWidth,
  strokeWidth,
  nodeSize,
  sourceNode,
  targetNode,
  isSubtractArrows = true,
}: {
  arrowsWidth: number
  strokeWidth: number
  nodeSize: number
  sourceNode: {
    x: number
    y: number
  }
  targetNode: {
    x: number
    y: number
  }
  isSubtractArrows?: boolean
}) {
  const r =
    nodeSize / 2 + (isSubtractArrows ? arrowsWidth * (strokeWidth * 2 + 4) : 0)
  const distance =
    Math.sqrt(
      Math.pow(sourceNode.x - targetNode.x, 2) +
        Math.pow(sourceNode.y - targetNode.y, 2)
    ) || 1
  const dX = ((sourceNode.x - targetNode.x) * r) / distance
  const dY = ((sourceNode.y - targetNode.y) * r) / distance

  return { x: sourceNode.x - dX, y: sourceNode.y - dY }
}

export function getPath({
  arrowsWidth,
  strokeWidth,
  edge,
  edges,
  sNodeSize,
  tNodeSize,
  sourceNode,
  targetNode,
}: {
  arrowsWidth: number
  strokeWidth: number
  edge: NormalGraphEdge
  edges: NormalGraphEdge[]
  sNodeSize?: number
  tNodeSize?: number
  sourceNode?: {
    x: number
    y: number
    size: number[]
    id: string
  }
  targetNode?: {
    x: number
    y: number
    size: number[]
    id: string
  }
}) {
  if (sourceNode == null || targetNode == null) {
    throw new Error(`edge ${edge.id} does not have a start and end node`)
  }

  const start = getLinkPoint({
    arrowsWidth,
    strokeWidth,
    nodeSize: sourceNode.size[0] || sNodeSize!,
    sourceNode,
    targetNode,
    isSubtractArrows: false,
  })
  const end = getLinkPoint({
    arrowsWidth,
    strokeWidth,
    nodeSize: targetNode.size[0] || tNodeSize!,
    sourceNode: targetNode,
    targetNode: sourceNode,
  })
  const sameEdges = sortBy(
    filter(
      edges,
      (edge) =>
        (edge.sourceNode?.id === sourceNode.id &&
          edge.targetNode?.id === targetNode.id) ||
        (edge.targetNode?.id === sourceNode.id &&
          edge.sourceNode?.id === targetNode.id)
    ),
    'id'
  )
  const edgeLength = sameEdges.length
  const edgeIndex = sameEdges.findIndex(({ id }) => edge.id === id)
  const distance =
    Math.sqrt(Math.pow(start.x - end.x, 2) + Math.pow(start.y - end.y, 2)) || 1

  if (edgeLength % 2 !== 0 && edgeIndex === 0) {
    return `M${start.x},${start.y} L${end.x},${end.y}`
  }

  const cpd = (60 * Math.ceil(edgeIndex / 2)) / distance // 向量方向长度系数
  const v1 = [
    (end.x + start.x) * 0.5 - start.x,
    (end.y + start.y) * 0.5 - start.y,
  ]

  let coefficient = edgeIndex % 2 === 0 ? 1 : -1

  if (sameEdges.length > 0 && sourceNode.id !== sameEdges[0].sourceNode?.id) {
    coefficient = -coefficient
  }

  const v2 = [-v1[1] * cpd * coefficient, v1[0] * cpd * coefficient]
  // 控制点
  const cp = {
    x: v1[0] + v2[0] + start.x,
    y: v1[1] + v2[1] + start.y,
  }
  const sp = getLinkPoint({
    arrowsWidth,
    strokeWidth,
    nodeSize: sourceNode.size[0] || sNodeSize!,
    sourceNode: sourceNode,
    targetNode: cp,
    isSubtractArrows: false,
  })
  const tp = getLinkPoint({
    arrowsWidth,
    strokeWidth,
    nodeSize: targetNode.size[0] || tNodeSize!,
    sourceNode: targetNode,
    targetNode: cp,
  })

  return `M${sp.x},${sp.y} Q${cp.x}, ${cp.y} ${tp.x}, ${tp.y}`
}
